import React, { useRef, useState, useEffect } from "react";
import { motion, useInView } from "framer-motion";
import { useLang } from "@/i18n";
import { useCursor } from "@/context/CursorContext";

const spanClass = {
  "lg-12": "lg:col-span-12",
  "lg-8": "lg:col-span-7",
  "lg-6": "lg:col-span-6",
  "lg-4": "lg:col-span-5",
};

const heightClass = {
  "lg-12": "aspect-[21/9]",
  "lg-8": "aspect-[16/10]",
  "lg-6": "aspect-[3/2]",
  "lg-4": "aspect-[4/5]",
};

export const ProjectCard = ({ project, index, onOpen }) => {
  const { lang } = useLang();
  const { setCursor, reset } = useCursor();
  const videoRef = useRef(null);
  const wrapRef = useRef(null);
  const inView = useInView(wrapRef, { amount: 0.5 });
  const [hovered, setHovered] = useState(false);

  const role = lang === "tr" ? project.role_tr : project.role_en;

  // Auto-play muted preview when in view; also on hover
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (inView) {
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
    } else {
      v.pause();
    }
  }, [inView]);

  const active = inView || hovered;

  return (
    <motion.article
      ref={wrapRef}
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay: (index % 2) * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className={`group relative col-span-1 md:col-span-12 ${spanClass[project.span] || "lg:col-span-6"}`}
      data-testid={`project-card-${project.slug}`}
    >
      <button
        onClick={() => onOpen(project)}
        onMouseEnter={() => { setHovered(true); setCursor("play"); }}
        onMouseLeave={() => { setHovered(false); reset(); }}
        className="block w-full text-left"
        data-testid={`project-open-${project.slug}`}
      >
        <div className={`relative w-full overflow-hidden bg-surface ${heightClass[project.span] || "aspect-[16/10]"}`}>
          <img
            src={project.poster}
            alt={project.title}
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover grayscale"
          />
          {project.preview && (
            <video
              ref={videoRef}
              src={project.preview}
              muted
              loop
              playsInline
              preload="metadata"
              poster={project.poster}
              className={`absolute inset-0 h-full w-full object-cover transition-transform duration-700 ${
                hovered ? "scale-105" : "scale-100"
              } ${active ? "opacity-100" : "opacity-0"}`}
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-void/70 via-transparent to-transparent" />

          <span className="absolute right-5 top-5 rounded-full bg-void/50 px-2.5 py-1 font-mono text-[10px] tracking-[0.25em] uppercase text-bone backdrop-blur-sm">
            {project.year}
          </span>
        </div>

        <div className="flex items-end justify-between gap-4 pt-5">
          <div className="min-w-0 flex-1">
            <h3 className="font-work text-3xl md:text-5xl font-medium uppercase text-bone group-hover:text-navy-ink transition-colors duration-300 break-words [overflow-wrap:anywhere]">
              {project.title}
            </h3>
            <p className="mt-2 font-mono text-[11px] tracking-[0.15em] uppercase text-bone/45">
              {project.client ? `${project.client} — ${role}` : role}
            </p>
          </div>
          <span className="shrink-0 font-display text-4xl text-bone/20 group-hover:text-navy-ink transition-all duration-300 group-hover:-translate-y-1 group-hover:translate-x-1">
            ↗
          </span>
        </div>
      </button>
    </motion.article>
  );
};
